import type { TextInputSuffix } from '@ksyos/design-system';
import type { PaginationEventHandlersParams } from '@ksyos/design-system/dist/components/pagination';
import {
    ActionBar,
    Box,
    Button,
    ButtonRow,
    Checkbox,
    DropdownFilter,
    Heading,
    Link,
    Loader,
    Pagination,
    Spacer,
    Table,
    TableBody,
    TableCell,
    TableCellSelectRow,
    TableContainer,
    TableHead,
    TableHeader,
    TableHeaderSelectAll,
    TableRow,
    Text,
    TextInput,
} from '@ksyos/design-system';
import { useNavigate } from '@ksyos/router';
import { useEffect, useMemo, useState } from 'react';
import type { EpisodeCategory } from '@common/enums/episodeCategory';
import type { IGroup } from '@frontend/components/interfaces/group';
import type { ValidationErrors } from '@frontend/utils';
import { ManagerAuthorization } from '@common/enums';
import { UrlBuilder } from '@common/utils/urlBuilder';
import { deviceAccessoryFiltersValidation, type IDeviceAccessoryFilters } from '@common/validation/deviceAccessory';
import { BadgeWithLabel } from '@frontend/components/elements/BadgeWithLabel';
import { Figure } from '@frontend/components/elements/figure/Figure';
import { fetchJson } from '@frontend/store/fetch';
import { emptyValidationErrors, hasOneAuthorization } from '@frontend/utils';
import { useAbortableFetch } from '@frontend/utils/hooks/useAbortableFetch';
import { handleValidatePeriod } from '@frontend/utils/validateDatePeriod';
import type { DeviceStatus } from '../../../../common/enums/deviceStatus';
import type { IOrganization } from '../../interfaces/organization';
import { StyledBox } from '../../../components/elements/styledBox';
import { translateKey } from '../../../translate';
import { TextWithMatchingTermInBold } from '../../elements/common';
import { NullableFormattedDate } from '../../elements/NullableFormattedDate';
import { PeriodFilter } from '../finance/financing/financingSearchFilters/PeriodFilter';

interface IDeviceAccessoryType {
    name: string;
    serviceEpisodeCategoriesNames: EpisodeCategory[];
}

interface IDeviceAccessory {
    deviceAccessoryOrganization: {
        organization?: Pick<IOrganization, 'id' | 'txcsId' | 'name'>;
    };
    deviceAccessoryType: IDeviceAccessoryType;
    group?: Pick<IGroup, 'id' | 'name'>;
    id: string;
    referenceNumber?: string;
    serialNumber?: string;
    status: DeviceStatus;
    updated_at?: string;
}

const emptyDeviceAccessoriesFilters: IDeviceAccessoryFilters = {};

export function DeviceAccessoriesList() {
    const navigate = useNavigate();
    const [items, setItems] = useState<IDeviceAccessory[]>([]);
    const [deviceAccessoryFilters, setDeviceAccessoryFilters] =
        useState<IDeviceAccessoryFilters>(emptyDeviceAccessoriesFilters);
    const [episodeCategoryFilterValues, setEpisodeCategoryFilterValues] = useState<EpisodeCategory[]>([]);
    const [page, setPage] = useState<number>(1);
    const [total, setTotal] = useState<number>(0);
    const [isExporting, setIsExporting] = useState<boolean>(false);
    const [itemsPerPage, setItemsPerPage] = useState<number>(15);
    const [isSearching, setIsSearching] = useState<boolean>(false);
    const [areAllChecked, setAreAllChecked] = useState<boolean>(false);
    const [selectedItems, setSelectedItems] = useState<string[]>([]);
    const [errors, setErrors] = useState<ValidationErrors>(emptyValidationErrors);

    const abortableFetch = useAbortableFetch();

    const isAddEditAllowed = useMemo(() => hasOneAuthorization(ManagerAuthorization.devicesAddEdit), []);

    useEffect(() => {
        loadDeviceAccessories({});
        loadEpisodeCategoryFilterValues();
    }, [itemsPerPage]);

    useEffect(() => {
        if (selectedItems.length === total && total > 0) {
            setAreAllChecked(true);
        }
    }, [selectedItems]);

    useEffect(() => {
        if (areAllChecked) {
            setSelectedItems([]);
        }
    }, [areAllChecked]);

    const suffixProp: TextInputSuffix = {
        icon: 'search',
        labelText: 'aria labelled text',
        onSuffixClick: () => {
            applySearchFilter();
        },
    };

    return (
        <>
            <StyledBox>
                <TextInput
                    onChange={(e) =>
                        setDeviceAccessoryFilters({ ...deviceAccessoryFilters, searchText: e.target.value })
                    }
                    onEnterKeyPress={applySearchFilter}
                    onReset={() => handleChangeAndUpdateSearchFilter('searchText', null)}
                    placeholder={translateKey('deviceAccessories.searchPlaceholder')}
                    suffix={suffixProp}
                    value={deviceAccessoryFilters.searchText ?? ''}
                    variant="outline"
                />
                <DropdownFilter
                    count={deviceAccessoryFilters.episodeCategories?.length}
                    label={translateKey('devices.filterOnEpisodeCategory')}
                    onReset={() => handleChangeAndUpdateSearchFilter('episodeCategories', [])}
                    onSubmit={applySearchFilter}
                >
                    {renderEpisodeCategoryDropdown()}
                </DropdownFilter>
                <PeriodFilter
                    endDate={deviceAccessoryFilters.endDate}
                    errors={errors}
                    onChangeDate={(name, value) =>
                        handleChangeAndUpdateSearchFilter(
                            name === 'startDate' ? 'startDate' : 'endDate',
                            value ?? undefined
                        )
                    }
                    onValidate={(name, value) =>
                        handleValidatePeriod({
                            datePeriod: {
                                endDate: name === 'endDate' ? value : deviceAccessoryFilters.endDate,
                                startDate: name === 'startDate' ? value : deviceAccessoryFilters.startDate,
                            },
                            errors,
                            schema: deviceAccessoryFiltersValidation,
                            setErrors,
                        })
                    }
                    startDate={deviceAccessoryFilters.startDate}
                />
                <Box
                    alignSelf="center"
                    ml={4}
                >
                    <Text
                        size="sm"
                        whiteSpace="nowrap"
                    >
                        <Link
                            color="subtle"
                            fontWeight="normal"
                            onClick={clearFilters}
                            variant="button"
                        >
                            {translateKey('common.deleteAll')}
                        </Link>
                    </Text>
                </Box>
            </StyledBox>
            {items?.length > 0 && !isExporting && (
                <>
                    <TableContainer>
                        <Table spacing="medium">
                            <TableHead>
                                <TableRow>
                                    <TableHeaderSelectAll
                                        checked={selectedItems.length > 0 || areAllChecked}
                                        indeterminate={!areAllChecked}
                                        onSelect={() => {
                                            setSelectedItems([]);
                                            setAreAllChecked(!areAllChecked);
                                        }}
                                    />
                                    <TableHeader>{translateKey('deviceAccessories.accessory')}</TableHeader>
                                    <TableHeader>{translateKey('devices.serialNumber')}</TableHeader>
                                    <TableHeader>{translateKey('episodeCategory.episodeCategory')}</TableHeader>
                                    <TableHeader>{translateKey('organization.organization')}</TableHeader>
                                    <TableHeader>{translateKey('organization.organization')} ID</TableHeader>
                                    <TableHeader>{translateKey('deviceAccessories.referenceNumber')}</TableHeader>
                                    <TableHeader>{translateKey('common.dateAdded')}</TableHeader>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {items.map((item) => {
                                    return (
                                        <TableRow
                                            key={item.id}
                                            onClick={
                                                isAddEditAllowed
                                                    ? () => navigate(`/devices/accessories/${item.id}`)
                                                    : undefined
                                            }
                                        >
                                            <TableCellSelectRow
                                                isSelected={isRowSelected(item.id) || areAllChecked}
                                                onClick={(e) => e.stopPropagation()}
                                                onSelect={() => handleSelectRow(item.id)}
                                            />
                                            <TableCell>{item.deviceAccessoryType.name}</TableCell>
                                            <TableCell>
                                                <TextWithMatchingTermInBold
                                                    searchTerm={deviceAccessoryFilters.searchText}
                                                    text={item.serialNumber}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                {Array.from(
                                                    new Set<string>(
                                                        item.deviceAccessoryType.serviceEpisodeCategoriesNames
                                                            .map((episodeCategory) =>
                                                                translateKey(`episodeCategoryEnum.${episodeCategory}`)
                                                            )
                                                            .sort()
                                                    )
                                                ).join(', ')}
                                            </TableCell>
                                            <TableCell>
                                                <TextWithMatchingTermInBold
                                                    searchTerm={deviceAccessoryFilters.searchText}
                                                    text={item.deviceAccessoryOrganization?.organization?.name}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <TextWithMatchingTermInBold
                                                    searchTerm={deviceAccessoryFilters.searchText}
                                                    text={item.deviceAccessoryOrganization?.organization?.txcsId}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <TextWithMatchingTermInBold
                                                    searchTerm={deviceAccessoryFilters.searchText}
                                                    text={item.referenceNumber}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                {item.updated_at ? (
                                                    <NullableFormattedDate value={item.updated_at} />
                                                ) : (
                                                    '-'
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Pagination
                        isDisabled={isSearching}
                        onChange={handleChangePagination}
                        page={page}
                        pageSize={itemsPerPage}
                        totalItems={total}
                    />
                </>
            )}
            <ActionBar
                isOpen={selectedItems.length !== 0 || areAllChecked}
                onClose={cleanSelectedRows}
            >
                <Box
                    alignItems="center"
                    display="flex"
                    gap={3}
                    justifyContent="space-between"
                >
                    <BadgeWithLabel
                        color="inverse"
                        label={translateKey('common.isSelected')}
                        value={areAllChecked ? total.toString() : selectedItems.length.toString()}
                    />
                    <Button
                        onClick={async () => await handleExport()}
                        variant="solid"
                    >
                        {translateKey('common.export')}
                    </Button>
                </Box>
            </ActionBar>
            {items?.length === 0 && !isSearching && (
                <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                >
                    <Figure
                        alt="Not found"
                        centered
                        imageUrl="../../../../../assets/images/not-found-search.svg"
                    />
                    <Heading
                        mb={6}
                        size="medium"
                        textAlign="center"
                    >
                        {translateKey('common.noSearchResults')}
                    </Heading>
                    <ButtonRow justify="center">
                        <Button onClick={clearFilters}>{translateKey('common.deleteSearchTerms')}</Button>
                    </ButtonRow>
                </Box>
            )}
            {isExporting && (
                <Loader
                    className="mt-3 mx-auto"
                    size="large"
                />
            )}
        </>
    );

    async function handleChangePagination({ page: newPage, pageSize }: PaginationEventHandlersParams) {
        setPage(newPage);
        setItemsPerPage(pageSize);
        await loadDeviceAccessories({ customPage: newPage, pageSize });
    }

    async function loadDeviceAccessories({
        customPage,
        customFilters,
        pageSize,
    }: {
        customFilters?: IDeviceAccessoryFilters;
        customPage?: number;
        pageSize?: number;
    }) {
        const urlBuilder = new UrlBuilder('/api/devices/accessories');

        const filters = customFilters ?? deviceAccessoryFilters;
        const pageNumber = customPage ?? page;

        urlBuilder.add('page', pageNumber);
        urlBuilder.add('limit', pageSize || itemsPerPage);

        if (filters.searchText) {
            urlBuilder.add('searchText', filters.searchText);
        }

        if (filters.startDate) {
            urlBuilder.add('dateFrom', filters.startDate.toISOString());
        }

        if (filters.endDate) {
            urlBuilder.add('dateTo', filters.endDate.toISOString());
        }

        if (filters.episodeCategories?.length) {
            urlBuilder.add('episodeCategories', filters.episodeCategories as string[]);
        }

        try {
            setIsSearching(true);
            const result = await abortableFetch.fetchJson(urlBuilder.build(), 'GET');
            setItems(result.json.items);
            setTotal(result.json.total);
        } finally {
            setIsSearching(false);
        }
    }

    async function loadEpisodeCategoryFilterValues() {
        const result = await fetchJson('/api/devices/accessories/getEpisodeCategoryFilterValues', 'GET');
        const episodeCategories = result.json.items.map((item: { name: string }) => item.name);
        setEpisodeCategoryFilterValues(episodeCategories);
    }

    function applySearchFilter() {
        if (page !== 1) {
            setPage(1);
        }
        loadDeviceAccessories({ customPage: 1 });
    }

    function renderEpisodeCategoryDropdown() {
        return episodeCategoryFilterValues.map((episodeCategory) => {
            return (
                <Spacer
                    direction="vertical"
                    key={episodeCategory}
                    spacing="small"
                >
                    <Checkbox
                        checked={!!deviceAccessoryFilters.episodeCategories?.includes(episodeCategory)}
                        key={episodeCategory}
                        label={translateKey(`episodeCategoryEnum.${episodeCategory}`)}
                        onChange={(event) => {
                            const updatedEpisodeCategories = [...(deviceAccessoryFilters.episodeCategories ?? [])];
                            if (event.target.checked) {
                                updatedEpisodeCategories.push(episodeCategory);
                            } else {
                                const index = updatedEpisodeCategories.indexOf(episodeCategory);
                                updatedEpisodeCategories.splice(index, 1);
                            }
                            handleChangeSearchFilter('episodeCategories', updatedEpisodeCategories);
                        }}
                    />
                </Spacer>
            );
        });
    }

    function handleChangeSearchFilter<K extends keyof IDeviceAccessoryFilters>(
        name: K,
        value: IDeviceAccessoryFilters[K]
    ) {
        setDeviceAccessoryFilters({ ...deviceAccessoryFilters, [name]: value });
    }

    function handleChangeAndUpdateSearchFilter<K extends keyof IDeviceAccessoryFilters>(
        name: K,
        value: IDeviceAccessoryFilters[K]
    ) {
        const deviceAccessoryFilter = { ...deviceAccessoryFilters, [name]: value };
        if (page !== 1) {
            setPage(1);
        }
        setDeviceAccessoryFilters(deviceAccessoryFilter);
        loadDeviceAccessories({ customFilters: deviceAccessoryFilter, customPage: 1 });
    }

    function clearFilters() {
        setErrors(emptyValidationErrors);
        setDeviceAccessoryFilters(emptyDeviceAccessoriesFilters);
        loadDeviceAccessories({ customFilters: emptyDeviceAccessoriesFilters });
    }

    function isRowSelected(selectedId: string) {
        return !!selectedItems.find((id) => id === selectedId);
    }

    function handleSelectRow(id: string) {
        let newSelectedItems = [...selectedItems];

        if (areAllChecked) {
            newSelectedItems = items.map((accessory) => accessory.id).filter((accessoryId) => accessoryId !== id);
            setAreAllChecked(false);
        } else if (isRowSelected(id)) {
            const index = newSelectedItems.indexOf(id);
            newSelectedItems.splice(index, 1);
        } else {
            newSelectedItems.push(id);
        }

        setSelectedItems(newSelectedItems);
    }

    function cleanSelectedRows() {
        setSelectedItems([]);
        setAreAllChecked(false);
    }

    async function handleExport() {
        cleanSelectedRows();
        setIsExporting(true);
        const itemsToExport = areAllChecked ? undefined : selectedItems;

        const result = await fetchJson('/api/devices/accessories/exportList', 'POST', {
            deviceAccessoryFilters,
            itemsToExport,
        });
        window.open(`/api/exportDownloads/${result.json.exportDownloadId}/accessoires.xlsx`);

        setIsExporting(false);
        applySearchFilter();
    }
}
